import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Pen, Save } from "lucide-react";

interface EditableNameInputProps {
  initialValue: string;
  placeholder?: string;
  onSave: (newValue: string) => void;
  onFocusChange?: (focused: boolean) => void;
  className?: string;
  inputClassName?: string;
  showButton?: boolean;
  autoFocus?: boolean;
  preventDropdownClose?: boolean;
}

export default function EditableNameInput({
  initialValue,
  placeholder,
  onSave,
  onFocusChange,
  className = "",
  inputClassName = "",
  showButton = true,
  autoFocus = false,
  preventDropdownClose = false,
}: EditableNameInputProps) {
  const [inputText, setInputText] = useState(initialValue || "");
  const [inputFocused, setInputFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (inputFocused && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [inputFocused]);

  const handleFocusChange = (focused: boolean) => {
    setInputFocused(focused);
    onFocusChange?.(focused);
  };

  const handleSave = () => {
    if (inputText.trim() !== initialValue) {
      onSave(inputText.trim());
      handleFocusChange(false);
      return;
    }
    handleFocusChange(true);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSave();
    }
    if (e.key === "Escape") {
      setInputText(initialValue);
      handleFocusChange(false);
    }
  };

  return (
    <div
      className={`w-full flex items-center group/quick-edit justify-between ${className}`}
    >
      <div className="w-full flex flex-col pr-0">
        {!inputFocused && (
          <span
            onClick={() => handleFocusChange(true)}
            className="text-sm font-600 w-[170px] group-hover/quick-edit:border-foreground group-hover/quick-edit:w-[130px] truncate text-foreground border-b border-dotted border-transparent pb-0.5 cursor-text select-none"
          >
            {inputText}
          </span>
        )}
        {inputFocused && (
          <Input
            ref={inputRef}
            className={`w-full font-600 mb-[2px] !h-auto !border-x-0 !border-t-0 !pb-0 !rounded-none border-b border-dotted text-foreground h-[32px pr-0 pl-0 focus:ring-0 focus:outline-none focus-visible:ring-0 focus-visible:outline-none ${inputClassName}`}
            placeholder={placeholder || t("category-name")}
            value={inputText}
            onFocus={() => handleFocusChange(true)}
            onBlur={(e) => {
              // Prevent blur when interacting with dropdown items
              if (preventDropdownClose) {
                const relatedTarget = e.relatedTarget as HTMLElement;
                if (
                  relatedTarget &&
                  relatedTarget.closest('[role="menuitem"]')
                ) {
                  e.preventDefault();
                  inputRef.current?.focus();
                  return;
                }
              }
              handleFocusChange(false);
            }}
            onKeyDown={handleKeyDown}
            onChange={(e) => setInputText(e.target.value)}
          />
        )}
      </div>
      {!inputFocused && (
        <Button
          variant="outline"
          size="icon"
          className="border-none h-[24px] !bg-transparent items-center justify-center hidden group-hover/quick-edit:inline-flex "
          onClick={handleSave}
        >
          <Pen width="16px" height="16px" />
        </Button>
      )}
    </div>
  );
}
